import { NewsRibbonWrapper } from '@/context/NewsRibbon';
import { isEqual } from 'lodash';
import React, { useMemo, useRef, memo } from 'react';
import { hashObject } from '@/utils/helpers';
import { resolveRTC, resolveLink, resolveBool, resolveTheme } from '@/utils/resolvers';
import NewsRibbon from './NewsRibbon';
import CMSWrapper from '../shared/CMSWrapper';

const MappedNewsRibbon = memo(({ content, presentation, contentButtons }: any) => {
  const { backgroundColor, collapsible, datalayerVariant, titleValue } = presentation;
  const {
    detailContent,
    primaryCTA,
    secondaryCTA,
    bodyBanner = '',
    htmlBanner = '',
    scriptsBanner = '',
    stylesBanner = '',
    contentType = '',
  } = content;

  const ribbonHash = useMemo(
    () =>
      hashObject({
        content: {
          backgroundColor,
          detailContent,
          primaryCTA,
          secondaryCTA,
          collapsible,
          bodyBanner,
          htmlBanner,
          scriptsBanner,
          stylesBanner,
        },
        keys: [
          'backgroundColor',
          'detailContent',
          'primaryCTA',
          'secondaryCTA',
          'collapsible',
          'bodyBanner',
          'htmlBanner',
          'scriptsBanner',
          'stylesBanner',
        ],
      }),
    [
      backgroundColor,
      bodyBanner,
      collapsible,
      detailContent,
      htmlBanner,
      primaryCTA,
      scriptsBanner,
      secondaryCTA,
      stylesBanner,
    ],
  );

  const newsRibbonProps = useMemo(
    () => ({
      backgroundColor,
      detailContent,
      primaryCTA,
      secondaryCTA,
      collapsible,
      contentButtons,
      bodyBanner,
      htmlBanner,
      scriptsBanner,
      stylesBanner,
      template: contentType,
      datalayerVariant,
      titleValue,
    }),
    [
      backgroundColor,
      collapsible,
      contentButtons,
      detailContent,
      primaryCTA,
      secondaryCTA,
      bodyBanner,
      htmlBanner,
      scriptsBanner,
      stylesBanner,
      contentType,
      datalayerVariant,
      titleValue,
    ],
  );

  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: backgroundColor }),
    [backgroundColor],
  );

  return (
    <NewsRibbonWrapper hash={ribbonHash} type="local">
      <NewsRibbon {...newsRibbonProps} theme={theme} />
    </NewsRibbonWrapper>
  );
}, isEqual);

const NewsRibbonCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        // SimpleRibbon
        detailContent: [null, resolveRTC],
        primaryCTA: [null, resolveLink],
        secondaryCTA: [null, resolveLink],
        // BannerRibbon
        bodyContent: ['bodyBanner', resolveRTC],
        html: 'htmlBanner',
        scripts: 'scriptsBanner',
        styles: 'stylesBanner',
        contentType: null,
      },
    ],
    componentParameterMap: [
      'presentation',
      {
        'ribbon-background-color': 'backgroundColor',
        collapsible: [null, resolveBool],
        'variant-label': 'datalayerVariant',
        title: 'titleValue',
      },
    ],
  });

  const requiredFields = useRef({
    document: '<b>News Ribbon Content</b> required',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });
  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedNewsRibbon}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default NewsRibbonCMSWrapper;
